import "./App.css";
import { useEffect } from "react";
import { trackPageview } from "./lib/analytics";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "./lib/auth";
import { Toaster } from "./components/ui/sonner";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import CookieConsent from "./components/CookieConsent";
import { ConfirmHost } from "./components/ConfirmDialog";
import ScrollToTop from "./components/ScrollToTop";
import HomePage from "./pages/HomePage";
import CoursesPage from "./pages/CoursesPage";
import CourseDetailPage from "./pages/CourseDetailPage";
import CourseThemePage from "./pages/CourseThemePage";
import GlossaryLetterPage from "./pages/GlossaryLetterPage";
import SimulatorsPage from "./pages/SimulatorsPage";
import LoginPage from "./pages/LoginPage";
import AuthCallback from "./pages/AuthCallback";
import AdminUsersPage from "./pages/AdminUsersPage";
import AdminSecurityPage from "./pages/AdminSecurityPage";
import AdminStatsPage from "./pages/AdminStatsPage";
import UpgradePage from "./pages/UpgradePage";
import AccountPage from "./pages/AccountPage";
import LegalPage from "./pages/LegalPage";
import PaymentStatusPage from "./pages/PaymentStatusPage";
import ArticlesPage from "./pages/ArticlesPage";
import ArticleDetailPage from "./pages/ArticleDetailPage";
import AdminArticlesPage from "./pages/AdminArticlesPage";
import AdminArticleEditorPage from "./pages/AdminArticleEditorPage";
import AdminCoursesPage from "./pages/AdminCoursesPage";
import AdminCourseEditorPage from "./pages/AdminCourseEditorPage";
import AdminCourseThemesPage from "./pages/AdminCourseThemesPage";
import SideBPortfoliosPage from "./pages/SideBPortfoliosPage";
import SideBPortfolioDetailPage from "./pages/SideBPortfolioDetailPage";
import AdminSideBPortfoliosPage from "./pages/AdminSideBPortfoliosPage";
import AdminReviewsPage from "./pages/AdminReviewsPage";
import AdminIdeasPage from "./pages/AdminIdeasPage";
import AdminNewsletterPage from "./pages/AdminNewsletterPage";
import AdminHomeCardsPage from "./pages/AdminHomeCardsPage";
import AdminUnlockDialog from "./components/AdminUnlockDialog";
import { RequireAuth, RequireAdmin } from "./components/RequireAuth";

function AppRoutes() {
  // Detect Emergent OAuth callback synchronously (before any auth check race)
  const location = useLocation();
  useEffect(() => {
    trackPageview(location.pathname);
  }, [location.pathname]);
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route
        path="/admin/utilisateurs"
        element={<RequireAdmin><AdminUsersPage /></RequireAdmin>}
      />
      <Route
        path="/admin/securite"
        element={<RequireAuth><AdminSecurityPage /></RequireAuth>}
      />
      <Route
        path="/admin/statistiques"
        element={<RequireAdmin><AdminStatsPage /></RequireAdmin>}
      />
      <Route path="/strategies" element={<Navigate to="/portefeuilles-sideb" replace />} />
      <Route path="/portefeuille" element={<Navigate to="/portefeuilles-sideb" replace />} />
      <Route path="/portefeuille/admin" element={<Navigate to="/admin/portefeuilles-sideb" replace />} />
      <Route path="/backtest" element={<Navigate to="/simulateurs" replace />} />
      <Route path="/cours" element={<CoursesPage />} />
      <Route path="/cours/thematique/:slug" element={<CourseThemePage />} />
      <Route path="/cours/glossaire/:letter" element={<GlossaryLetterPage />} />
      <Route path="/cours/:slug" element={<CourseDetailPage />} />
      <Route path="/simulateurs" element={<SimulatorsPage />} />
      <Route path="/simulateurs/:toolId" element={<SimulatorsPage />} />
      <Route path="/upgrade" element={<UpgradePage />} />
      <Route path="/mentions-legales" element={<LegalPage kind="mentions" />} />
      <Route path="/cgv" element={<LegalPage kind="cgv" />} />
      <Route path="/confidentialite" element={<LegalPage kind="confidentialite" />} />
      <Route path="/account" element={<RequireAuth><AccountPage /></RequireAuth>} />
      <Route path="/payment/success" element={<PaymentStatusPage outcome="success" />} />
      <Route path="/payment/cancel" element={<PaymentStatusPage outcome="cancel" />} />
      <Route path="/articles" element={<ArticlesPage />} />
      <Route path="/articles/:slug" element={<ArticleDetailPage />} />
      <Route
        path="/admin/articles"
        element={<RequireAdmin><AdminArticlesPage /></RequireAdmin>}
      />
      <Route
        path="/admin/articles/nouveau"
        element={<RequireAdmin><AdminArticleEditorPage /></RequireAdmin>}
      />
      <Route
        path="/admin/articles/:id/modifier"
        element={<RequireAdmin><AdminArticleEditorPage /></RequireAdmin>}
      />
      <Route
        path="/admin/cours"
        element={<RequireAdmin><AdminCoursesPage /></RequireAdmin>}
      />
      <Route
        path="/admin/cours/thematiques"
        element={<RequireAdmin><AdminCourseThemesPage /></RequireAdmin>}
      />
      <Route
        path="/admin/cours/nouveau"
        element={<RequireAdmin><AdminCourseEditorPage /></RequireAdmin>}
      />
      <Route
        path="/admin/cours/:id/modifier"
        element={<RequireAdmin><AdminCourseEditorPage /></RequireAdmin>}
      />
      <Route path="/portefeuilles-sideb" element={<SideBPortfoliosPage />} />
      <Route path="/portefeuilles-sideb/:slug" element={<SideBPortfolioDetailPage />} />
      <Route
        path="/admin/portefeuilles-sideb"
        element={<RequireAdmin><AdminSideBPortfoliosPage /></RequireAdmin>}
      />
      <Route
        path="/admin/avis"
        element={<RequireAdmin><AdminReviewsPage /></RequireAdmin>}
      />
      <Route
        path="/admin/idees"
        element={<RequireAdmin><AdminIdeasPage /></RequireAdmin>}
      />
      <Route
        path="/admin/newsletter"
        element={<RequireAdmin><AdminNewsletterPage /></RequireAdmin>}
      />
      <Route
        path="/admin/home-cards"
        element={<RequireAdmin><AdminHomeCardsPage /></RequireAdmin>}
      />      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<Navigate to="/login" replace />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="*" element={<HomePage />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App pb-[calc(4rem+env(safe-area-inset-bottom))] md:pb-0">
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <Navbar />
          <main>
            <AppRoutes />
          </main>
          <Footer />
          <CookieConsent />
          <Toaster theme="dark" position="top-right" />
          <AdminUnlockDialog />
          <ConfirmHost />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
